import type { ResultOf } from '@graphql-typed-document-node/core'
// eslint-disable-next-line no-restricted-imports
import { useLocation } from '@remix-run/react'
import { atom, useAtomValue } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { ScrollableRestaurants } from '~/components/ScrollableRestaurants'
import LoadingSkeleton from '~/components/skeleton/sd/LoadingSkeleton'
import { isEmpty } from '~/lib/empty'
import { graphql } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { useInitAtom } from '~/lib/jotai'
import { recommendCardLinkPath } from '~/lib/path/path'
import { useIsPC } from '~/lib/ua'
import { css } from '~/styled-system/css'
import { GuideSection } from '../GuideSection'
import { BottomRestaurants } from '../pc/BottomRestaurants'

export const GetRecommendRestaurants = graphql(/* GraphQL */ `
  query GetRecommendRestaurants($restaurantId: RestaurantId!) {
    restaurant(restaurantId: $restaurantId) {
      recommendRestaurants(first: 5) {
        ...RestaurantRecommendCard
      }
    }
  }
`)

export type GetRecommendRestaurantsType = ResultOf<typeof GetRecommendRestaurants>

const restaurantId$ = atom<RestaurantId | undefined>(undefined)
const recommends$ = atomWithQuery<GetRecommendRestaurantsType>((get) => ({
  queryKey: ['GetRecommendRestaurants', get(restaurantId$)],
  queryFn: async ({ queryKey: [, restaurantId] }) => {
    const res = await fetch(`/api/v2/restaurants/${restaurantId}/recommends`)
    return res.json()
  },
  enabled: get(restaurantId$) !== undefined,
}))

export function RecommendRestaurants({ restaurantId }: { restaurantId: RestaurantId }) {
  useInitAtom(restaurantId$, restaurantId)
  const { data, isLoading } = useAtomValue(recommends$)
  const { search } = useLocation()
  const linkTo = (restaurantId: RestaurantId) => recommendCardLinkPath(restaurantId, search)
  const isPc = useIsPC()
  const Restaurants = isPc ? BottomRestaurants : ScrollableRestaurants
  if (isLoading) {
    return <LoadingSkeleton height="235px" />
  }
  const restaurants = data?.restaurant?.recommendRestaurants
  if (isEmpty(restaurants)) {
    return null
  }
  return (
    // FIXME: 上位のコンポーネントでpaddingを設定するべき
    //        <https://github.com/ikyucom/macaron-frontend/issues/4517>
    <div className={css({ py: '3', _pc: { py: '0' } })}>
      <GuideSection heading="こちらとよく一緒に閲覧されているレストラン" size="medium">
        <Restaurants restaurants={restaurants} linkTo={linkTo} />
      </GuideSection>
    </div>
  )
}
